import { useState, useEffect } from "react";
import { AlertTriangle, Clock, Undo2, RefreshCw } from "lucide-react";
import MetricCard from "@/components/MetricCard";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminFailures() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/admin/activations/failures").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const forceRefund = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/activations/${id}/force-refund`);
      toast.success("Estorno realizado e ativação marcada como falha.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(null); }
  };

  if (!data) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="admin-failures">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Painel de Falhas</h2>
        <Button variant="outline" size="sm" className="gap-2" onClick={load} data-testid="failures-reload"><RefreshCw className="h-4 w-4" /> Atualizar</Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard icon={AlertTriangle} label="Ativações com erro" value={data.total_failed} testid="failures-total" />
        <MetricCard icon={AlertTriangle} label="Dados incorretos" value={data.total_incorrect || 0} testid="failures-incorrect" />
        <MetricCard icon={Clock} label="Pendentes" value={data.total_pending} testid="failures-pending" />
        <MetricCard icon={AlertTriangle} label="Apps com problema" value={data.by_app.length} testid="failures-apps" />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border p-4"><h3 className="font-heading font-semibold">Falhas por aplicativo</h3></div>
        <div className="p-3 md:hidden">
          <MobileCards testid="fail-app-cards">
            {data.by_app.length === 0 && <EmptyCard>Nenhuma falha registrada. 🎉</EmptyCard>}
            {data.by_app.map((r, i) => (
              <DataCard
                key={i}
                testid={`fail-app-card-${i}`}
                title={r.app_name}
                badge={<span className="rounded-full bg-destructive/15 px-2 py-0.5 text-sm font-semibold text-destructive">{r.count}</span>}
                rows={[
                  { label: "Última ocorrência", value: r.last_at ? new Date(r.last_at).toLocaleString("pt-BR") : "—", full: true },
                  { label: "Último erro", value: r.last_error || "—", full: true, wrap: true },
                ]}
              />
            ))}
          </MobileCards>
        </div>
        <div className="hidden md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Aplicativo</TableHead><TableHead>Falhas</TableHead><TableHead>Último erro</TableHead><TableHead>Última ocorrência</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.by_app.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Nenhuma falha registrada. 🎉</TableCell></TableRow>}
            {data.by_app.map((r, i) => (
              <TableRow key={i} data-testid={`fail-app-${i}`}>
                <TableCell className="font-medium">{r.app_name}</TableCell>
                <TableCell><span className="rounded-full bg-destructive/15 px-2 py-0.5 text-sm font-semibold text-destructive">{r.count}</span></TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">{r.last_error || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{r.last_at ? new Date(r.last_at).toLocaleString("pt-BR") : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border p-4"><h3 className="font-heading font-semibold">Ativações recentes com problema</h3></div>
        <div className="p-3 md:hidden">
          <MobileCards testid="fail-recent-cards">
            {data.recent.length === 0 && <EmptyCard>Nada por aqui.</EmptyCard>}
            {data.recent.map((a) => (
              <DataCard
                key={a.id}
                testid={`fail-recent-card-${a.id}`}
                title={a.app_name}
                subtitle={a.mac_app}
                badge={<StatusBadge status={a.status} />}
                rows={[
                  { label: "Créditos", value: <span className="font-num">{a.credits_charged}</span> },
                  { label: "Cliente", value: a.guest_email || (a.user_id ? "conta" : "—") },
                ]}
                actions={(a.credits_charged || 0) > 0 && a.user_id ? (
                  <Button variant="outline" size="sm" className="flex-1 gap-1" disabled={busyId === a.id} onClick={() => forceRefund(a.id)} data-testid={`force-refund-m-${a.id}`}>
                    <Undo2 className="h-4 w-4" /> Estornar
                  </Button>
                ) : <span className="text-xs text-muted-foreground">sem crédito reservado</span>}
              />
            ))}
          </MobileCards>
        </div>
        <div className="hidden md:block">
        <Table>
          <TableHeader><TableRow>
            <TableHead>App</TableHead><TableHead>Dado</TableHead><TableHead>Cliente</TableHead><TableHead>Créditos</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ação</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.recent.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Nada por aqui.</TableCell></TableRow>}
            {data.recent.map((a) => (
              <TableRow key={a.id} data-testid={`fail-recent-${a.id}`}>
                <TableCell className="font-medium">{a.app_name}</TableCell>
                <TableCell className="text-muted-foreground">{a.mac_app}</TableCell>
                <TableCell className="text-muted-foreground">{a.guest_email || (a.user_id ? "conta" : "—")}</TableCell>
                <TableCell>{a.credits_charged}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell>
                <TableCell className="text-right">
                  {(a.credits_charged || 0) > 0 && a.user_id ? (
                    <Button variant="outline" size="sm" className="gap-1" disabled={busyId === a.id} onClick={() => forceRefund(a.id)} data-testid={`force-refund-${a.id}`}>
                      <Undo2 className="h-4 w-4" /> Estornar
                    </Button>
                  ) : <span className="text-xs text-muted-foreground">sem crédito reservado</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}
